import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sendToast } from 'screens/Dashboard/actions';
import { ApiServiceV2 } from 'services';

const DEFAULT_TENANT_SEED_PACKS = Object.freeze(['core-catalogs']);
const TERMINAL_TENANT_JOB_STATUSES = Object.freeze(['ready', 'failed']);
const POLLABLE_TENANT_JOB_STATUSES = Object.freeze(['pending', 'running']);
const REFRESH_EVENT = 'synapse:tenants:refresh';

const createInitialForm = () => ({
	companyName: '',
	organizationName: '',
	schemaSlug: '',
	timezone: 'America/Costa_Rica',
	country: 'Costa Rica',
	baselineVersion: 'v2',
	seedPacks: [...DEFAULT_TENANT_SEED_PACKS],
	adminName: '',
	adminIdc: '',
	adminEmail: '',
});

const normalizeTenantStatusFilter = statusFilter => {
	if (!statusFilter || statusFilter === 'all') return undefined;
	return statusFilter;
};

const shouldPollProvisioningJob = job => {
	if (!job || !job.status) return false;
	return POLLABLE_TENANT_JOB_STATUSES.includes(job.status);
};

const extractTenantCollection = response => {
	if (Array.isArray(response)) return response;
	if (Array.isArray(response?.data)) return response.data;
	return [];
};

const normalizeTenantUiError = (
	error,
	fallbackMessage = 'Ocurrió un error inesperado.',
) => {
	if (!error) return fallbackMessage;
	if (typeof error === 'string') return error;
	if (Array.isArray(error)) {
		const messages = error
			.map(item => normalizeTenantUiError(item, ''))
			.filter(Boolean);
		return messages.length > 0 ? messages.join(' | ') : fallbackMessage;
	}
	if (typeof error?.message === 'string' && error.message.trim()) {
		return error.message;
	}
	if (typeof error?.error === 'string' && error.error.trim()) {
		return error.error;
	}
	try {
		return JSON.stringify(error);
	} catch (serializationError) {
		return fallbackMessage;
	}
};

const findProvisioningJob = (jobs, jobId) =>
	(Array.isArray(jobs) ? jobs : []).find(job => job.id === jobId) || null;

const resolveTenantPollingState = ({ jobs, selectedJobId, pollingJobId }) => {
	const targetJobId = pollingJobId || selectedJobId || null;
	const targetJob = findProvisioningJob(jobs, targetJobId);

	if (!targetJob) {
		return {
			isPolling: false,
			pollingJobId: null,
		};
	}

	return {
		isPolling: shouldPollProvisioningJob(targetJob),
		pollingJobId: shouldPollProvisioningJob(targetJob) ? targetJob.id : null,
	};
};

const buildCreateProvisionPayload = form => ({
	companyName: String(form.companyName || '').trim(),
	organizationName: String(form.organizationName || '').trim(),
	schemaSlug: String(form.schemaSlug || '')
		.trim()
		.toLowerCase(),
	timezone: String(form.timezone || '').trim(),
	country: String(form.country || '').trim(),
	baselineVersion: String(form.baselineVersion || 'v2').trim(),
	seedPacks:
		Array.isArray(form.seedPacks) && form.seedPacks.length > 0
			? form.seedPacks
			: [...DEFAULT_TENANT_SEED_PACKS],
	admin: {
		name: String(form.adminName || '').trim(),
		idc: String(form.adminIdc || '').trim(),
		email: String(form.adminEmail || '').trim(),
	},
});

const validateCreateProvisionPayload = payload => {
	if (!payload.companyName) return 'Debes indicar el nombre de la compañía.';
	if (!payload.organizationName)
		return 'Debes indicar el nombre de la organización.';
	if (!payload.schemaSlug) return 'Debes indicar el schema del tenant.';
	if (!/^[a-z0-9_]+$/.test(payload.schemaSlug)) {
		return 'El schema sólo puede contener minúsculas, números y guiones bajos.';
	}
	if (!payload.timezone) return 'Debes indicar la zona horaria.';
	if (!payload.country) return 'Debes indicar el país.';
	if (!payload.admin?.name) return 'Debes indicar el nombre del admin inicial.';
	if (!payload.admin?.idc) return 'Debes indicar la identificación del admin.';
	if (!payload.admin?.email) return 'Debes indicar el email del admin.';
	if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.admin.email)) {
		return 'Debes indicar un email válido para el admin.';
	}
	return null;
};

const useTenantsApi = () => {
	const dispatch = useDispatch();
	const [control, setControl] = useState({
		statusFilter: 'all',
		limit: 50,
		selectedJobId: null,
		pollingJobId: null,
		isPolling: false,
		pollingIntervalMs: 5000,
		isFetchingJobs: false,
	});
	const [createForm, setCreateForm] = useState({
		...createInitialForm(),
		isSubmitting: false,
		error: null,
	});
	const [jobs, setJobs] = useState({ data: [], error: null });
	const [jobSteps, setJobSteps] = useState({
		byJobId: {},
		isFetching: false,
		error: null,
	});

	const fetchProvisioningJobs = useCallback(async () => {
		setControl(current => ({ ...current, isFetchingJobs: true }));
		try {
			const response = await ApiServiceV2.get({
				url: '/admin/tenants/provisioning-jobs',
				params: {
					limit: control.limit,
					status: normalizeTenantStatusFilter(control.statusFilter),
				},
			});

			const nextJobs = extractTenantCollection(response);
			setJobs({ data: nextJobs, error: null });
			setControl(current => {
				const selectedJob =
					findProvisioningJob(nextJobs, current.selectedJobId) ||
					nextJobs[0] ||
					null;
				const pollingState = resolveTenantPollingState({
					jobs: nextJobs,
					selectedJobId: selectedJob?.id || current.selectedJobId,
					pollingJobId: current.pollingJobId,
				});

				return {
					...current,
					isFetchingJobs: false,
					selectedJobId: selectedJob?.id || null,
					...pollingState,
				};
			});
		} catch (error) {
			const message = normalizeTenantUiError(
				error,
				'No se pudieron cargar los jobs de provisioning.',
			);
			setJobs({ data: [], error: message });
			setControl(current => ({
				...current,
				isFetchingJobs: false,
				isPolling: false,
				pollingJobId: null,
			}));
		}
	}, [control.limit, control.statusFilter]);

	const fetchProvisioningJobSteps = useCallback(async jobId => {
		if (!jobId) return;
		setJobSteps(current => ({ ...current, isFetching: true, error: null }));
		try {
			const response = await ApiServiceV2.get({
				url: `/admin/tenants/provisioning-jobs/${jobId}/steps`,
			});
			const steps = extractTenantCollection(response);
			setJobSteps(current => ({
				...current,
				byJobId: {
					...current.byJobId,
					[jobId]: steps,
				},
				isFetching: false,
				error: null,
			}));
		} catch (error) {
			setJobSteps(current => ({
				...current,
				isFetching: false,
				error: normalizeTenantUiError(
					error,
					'No se pudieron cargar los steps del job.',
				),
			}));
		}
	}, []);

	const refresh = useCallback(() => {
		fetchProvisioningJobs();
	}, [fetchProvisioningJobs]);

	const onFormChange = useCallback((key, value) => {
		setCreateForm(current => ({ ...current, [key]: value, error: null }));
	}, []);

	const onStatusFilterChange = useCallback(statusFilter => {
		setControl(current => ({ ...current, statusFilter }));
	}, []);

	const onSelectJob = useCallback(jobId => {
		setControl(current => ({ ...current, selectedJobId: jobId }));
	}, []);

	const onSubmit = useCallback(async () => {
		const payload = buildCreateProvisionPayload(createForm);
		const error = validateCreateProvisionPayload(payload);

		if (error) {
			setCreateForm(current => ({ ...current, error }));
			dispatch(sendToast({ message: error, type: 'warn' }));
			return;
		}

		setCreateForm(current => ({ ...current, isSubmitting: true, error: null }));
		try {
			const response = await ApiServiceV2.post({
				url: '/admin/tenants/provision-requests',
				data: payload,
			});

			const createdJob = {
				id: response.jobId,
				status: response.status,
				schemaSlug: response.schemaSlug,
			};

			setCreateForm({
				...createInitialForm(),
				isSubmitting: false,
				error: null,
			});
			setJobs(current => ({
				data: [createdJob, ...(current.data || [])],
				error: null,
			}));
			setControl(current => ({
				...current,
				selectedJobId: createdJob.id,
				pollingJobId: createdJob.id,
				isPolling: true,
			}));

			dispatch(
				sendToast({
					message: `Solicitud creada. Job #${createdJob.id} en estado ${response.status}.`,
					type: 'success',
				}),
			);

			fetchProvisioningJobs();
			fetchProvisioningJobSteps(createdJob.id);
		} catch (error) {
			const message = normalizeTenantUiError(
				error,
				'No se pudo crear el tenant.',
			);
			setCreateForm(current => ({
				...current,
				isSubmitting: false,
				error: message,
			}));
			dispatch(sendToast({ message, type: 'error' }));
		}
	}, [createForm, dispatch, fetchProvisioningJobSteps, fetchProvisioningJobs]);

	useEffect(() => {
		fetchProvisioningJobs();
	}, [fetchProvisioningJobs]);

	useEffect(() => {
		if (!control.selectedJobId) return;
		fetchProvisioningJobSteps(control.selectedJobId);
	}, [control.selectedJobId, fetchProvisioningJobSteps]);

	useEffect(() => {
		if (!control.isPolling) return undefined;
		const timerId = setInterval(() => {
			fetchProvisioningJobs();
			if (control.selectedJobId) {
				fetchProvisioningJobSteps(control.selectedJobId);
			}
		}, control.pollingIntervalMs || 5000);

		return () => clearInterval(timerId);
	}, [
		control.isPolling,
		control.pollingIntervalMs,
		control.selectedJobId,
		fetchProvisioningJobSteps,
		fetchProvisioningJobs,
	]);

	useEffect(() => {
		const handleRefresh = () => refresh();
		window.addEventListener(REFRESH_EVENT, handleRefresh);
		return () => window.removeEventListener(REFRESH_EVENT, handleRefresh);
	}, [refresh]);

	const selectedJob = useMemo(
		() => findProvisioningJob(jobs.data || [], control.selectedJobId),
		[jobs.data, control.selectedJobId],
	);
	const selectedJobSteps = useMemo(
		() => jobSteps.byJobId?.[control.selectedJobId] || [],
		[jobSteps.byJobId, control.selectedJobId],
	);
	const isBusy =
		control.isFetchingJobs || createForm.isSubmitting || jobSteps.isFetching;
	const activeJobsCount = useMemo(
		() =>
			(jobs.data || []).filter(
				job => !TERMINAL_TENANT_JOB_STATUSES.includes(job.status),
			).length,
		[jobs.data],
	);

	return {
		control,
		createForm,
		jobs,
		jobSteps,
		selectedJob,
		selectedJobSteps,
		onFormChange,
		onSubmit,
		onStatusFilterChange,
		onSelectJob,
		refresh,
		isBusy,
		activeJobsCount,
		refreshEventName: REFRESH_EVENT,
	};
};

export default useTenantsApi;
